/**
 * Custom React Hook for Clinical Notes API
 */

import { useState, useEffect, useCallback } from 'react';
import clinicalNoteService from '../services/clinicalNoteService';
import {
  ClinicalNoteDTO,
  CreateClinicalNoteRequest,
  ClinicalNoteType,
  PageResponse,
} from '../types/clinicalNote.types';

interface UseClinicalNotesProps {
  residentId?: string;
  autoFetch?: boolean;
}

export const useClinicalNotes = ({ residentId, autoFetch = true }: UseClinicalNotesProps = {}) => {
  const [clinicalNotes, setClinicalNotes] = useState<ClinicalNoteDTO[]>([]);
  const [currentNote, setCurrentNote] = useState<ClinicalNoteDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 20;

  // Fetch all clinical notes with pagination
  const fetchClinicalNotes = useCallback(async (page: number = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response: PageResponse<ClinicalNoteDTO> = await clinicalNoteService.getAllClinicalNotes(page, pageSize);
      setClinicalNotes(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
      setCurrentPage(response.number);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch clinical notes');
      console.error('Error fetching clinical notes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch clinical notes by resident
  const fetchClinicalNotesByResident = useCallback(async (resId: string) => {
    setLoading(true);
    setError(null);
    try {
      const notes = await clinicalNoteService.getClinicalNotesByResident(resId);
      setClinicalNotes(notes);
      setTotalElements(notes.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch clinical notes for resident');
      console.error('Error fetching clinical notes by resident:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch clinical notes by type
  const fetchClinicalNotesByType = useCallback(async (noteType: ClinicalNoteType) => {
    setLoading(true);
    setError(null);
    try {
      const notes = await clinicalNoteService.getClinicalNotesByType(noteType);
      setClinicalNotes(notes);
      setTotalElements(notes.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch clinical notes by type');
      console.error('Error fetching clinical notes by type:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch clinical note by ID
  const fetchClinicalNoteById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const note = await clinicalNoteService.getClinicalNoteById(id);
      setCurrentNote(note);
      return note;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch clinical note');
      console.error('Error fetching clinical note:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new clinical note
  const createClinicalNote = useCallback(async (request: CreateClinicalNoteRequest) => {
    setSaving(true);
    setError(null);
    try {
      const newNote = await clinicalNoteService.createClinicalNote(request);
      // After creating, refresh list for the resident if provided to ensure persisted data is loaded
      if (request.residentId) {
        await fetchClinicalNotesByResident(request.residentId);
      } else {
        await fetchClinicalNotes(0);
      }
      setCurrentNote(newNote);
      return newNote;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create clinical note');
      console.error('Error creating clinical note:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  // Update clinical note
  const updateClinicalNote = useCallback(async (id: string, data: Partial<ClinicalNoteDTO>) => {
    setSaving(true);
    setError(null);
    try {
      const updatedNote = await clinicalNoteService.updateClinicalNote(id, data);
      // Refresh relevant list after update
      if (updatedNote?.residentId) {
        await fetchClinicalNotesByResident(updatedNote.residentId);
      } else {
        await fetchClinicalNotes(0);
      }
      if (currentNote?.id === id) {
        setCurrentNote(updatedNote);
      }
      return updatedNote;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update clinical note');
      console.error('Error updating clinical note:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentNote]);

  // Sign clinical note
  const signClinicalNote = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      const signedNote = await clinicalNoteService.signClinicalNote(id);
      setClinicalNotes((prev) =>
        prev.map((note) => (note.id === id ? signedNote : note))
      );
      if (currentNote?.id === id) {
        setCurrentNote(signedNote);
      }
      return signedNote;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to sign clinical note');
      console.error('Error signing clinical note:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentNote]);

  // Delete clinical note
  const deleteClinicalNote = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      await clinicalNoteService.deleteClinicalNote(id);
      // After delete, refetch current page or resident list
      if (currentNote?.residentId) {
        await fetchClinicalNotesByResident(currentNote.residentId);
      } else {
        await fetchClinicalNotes(0);
      }
      if (currentNote?.id === id) {
        setCurrentNote(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete clinical note');
      console.error('Error deleting clinical note:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentNote]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      if (residentId) {
        fetchClinicalNotesByResident(residentId);
      } else {
        fetchClinicalNotes(0);
      }
    }
  }, [residentId, autoFetch, fetchClinicalNotes, fetchClinicalNotesByResident]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    fetchClinicalNotes(page);
  }, [fetchClinicalNotes]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      fetchClinicalNotes(currentPage + 1);
    }
  }, [currentPage, totalPages, fetchClinicalNotes]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      fetchClinicalNotes(currentPage - 1);
    }
  }, [currentPage, fetchClinicalNotes]);

  return {
    // State
    clinicalNotes,
    currentNote,
    loading,
    saving,
    error,
    totalElements,
    totalPages,
    currentPage,
    pageSize,

    // Actions
    fetchClinicalNotes,
    fetchClinicalNotesByResident,
    fetchClinicalNotesByType,
    fetchClinicalNoteById,
    createClinicalNote,
    updateClinicalNote,
    signClinicalNote,
    deleteClinicalNote,
    handlePageChange,
    nextPage,
    prevPage,
    setCurrentNote,
    setClinicalNotes,
    setError,
  };
};